import React from 'react';
import { ChevronDown, Filter, PlusCircle, Search, X } from 'lucide-react';
import { useResponsive } from '../../contexts/ResponsiveContext';
import {
  COMMUNITY_TYPE_FILTER_OPTIONS,
  COMMUNITY_PILLAR_FILTER_OPTIONS,
} from '../../utils/communityConstants';

// Matches the stage filter select on the Leads / Clients page
const FilterSelect = ({ id, label, value, onChange, options, className = '' }) => (
  <div className={`relative shrink-0 ${className}`}>
    <label htmlFor={id} className="sr-only">
      {label}
    </label>
    <Filter className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full appearance-none rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-10 pr-9 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
      aria-label={label}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    <ChevronDown
      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
      aria-hidden
    />
  </div>
);

const CommunitySearchFilterBar = ({
  searchQuery,
  onSearchChange,
  activeType,
  onTypeChange,
  activePillar,
  onPillarChange,
  onNewPost,
}) => {
  const { isMobile } = useResponsive();

  return (
    <div className={`mb-6 flex ${isMobile ? 'flex-col' : 'items-center'} ${isMobile ? 'gap-2' : 'gap-3'}`}>
      {/* Search bar */}
      <div className={`relative ${isMobile ? 'w-full' : 'flex-1 min-w-0'}`}>
        <Search
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
          aria-hidden
        />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search posts by title, author, or content…"
          aria-label="Search posts"
          className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-10 pr-10 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent [&::-webkit-search-cancel-button]:hidden"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
            title="Clear search"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className={`flex gap-2 ${isMobile ? 'w-full' : ''}`}>
        <FilterSelect
          id="community-filter-type"
          label="Type"
          value={activeType}
          onChange={onTypeChange}
          options={COMMUNITY_TYPE_FILTER_OPTIONS}
          className={isMobile ? 'flex-1' : 'w-40'}
        />
        <FilterSelect
          id="community-filter-pillar"
          label="Pillar"
          value={activePillar}
          onChange={onPillarChange}
          options={COMMUNITY_PILLAR_FILTER_OPTIONS}
          className={isMobile ? 'flex-1' : 'w-44'}
        />

        {isMobile && onNewPost && (
          <button
            type="button"
            onClick={onNewPost}
            className="inline-flex items-center gap-1.5 px-3 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium shadow-sm transition-colors shrink-0"
            aria-label="New post"
          >
            <PlusCircle className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default CommunitySearchFilterBar;
